import * as React from "react";
import { Link, useLocation } from "react-router";
import { Sparkles, Send } from "lucide-react";
import { Alert, Button, Drawer, Textarea } from "@cm/ui";
import { api, ApiError } from "../../../lib/api";
import { useWsQuery } from "../../../lib/hooks";
import { useSession } from "../../../lib/session";
import { trackAction } from "../../../lib/usage";

interface Msg {
  id?: string;
  role: "user" | "assistant";
  content: string;
}

/**
 * Painel lateral da IA (V2-11): dúvidas sobre o passo atual da configuração e sobre os números do negócio.
 * A IA só explica e orienta; não executa nada. Abre pelo botão do topo ou por openAssistant(step).
 */
export function AssistantPanel() {
  const loc = useLocation();
  const { me } = useSession();
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<string | null>(null);
  const [text, setText] = React.useState("");
  const [local, setLocal] = React.useState<Msg[]>([]);
  const [pending, setPending] = React.useState(false);
  const [err, setErr] = React.useState<{ message: string; noCredits: boolean } | null>(null);
  const [spent, setSpent] = React.useState(0);
  const hist = useWsQuery<{ messages: Msg[]; ai: string; credits: { balance: number; per_question: number } | null }>(open ? "/api/app/guide/chat" : null, { retry: false });
  const balance = hist.data?.credits ? hist.data.credits.balance - spent : null;
  const listRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const h = (e: Event) => {
      setStep((e as CustomEvent).detail?.step ?? null);
      setOpen(true);
      trackAction("ai.panel_open");
    };
    window.addEventListener("cm:assistant-open", h);
    return () => window.removeEventListener("cm:assistant-open", h);
  }, []);
  React.useEffect(() => setLocal([]), [me?.active_workspace_id]);
  // Ao fechar, o histórico local é descartado: ao reabrir, vem do servidor (sem duplicar).
  React.useEffect(() => {
    if (!open) {
      setLocal([]);
      setSpent(0);
    }
  }, [open]);
  const messages = [...(hist.data?.messages ?? []), ...local];
  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length, open]);
  const ask = async () => {
    const m = text.trim();
    if (!m) return;
    setText("");
    setErr(null);
    setLocal((l) => [...l, { role: "user", content: m }]);
    setPending(true);
    trackAction("ai.ask");
    try {
      const r = await api.post<{ answer: string }>("/api/app/guide/ask", { message: m, step, page: loc.pathname });
      setLocal((l) => [...l, { role: "assistant", content: r.answer }]);
      if (hist.data?.credits) setSpent((n) => n + hist.data!.credits!.per_question);
    } catch (e) {
      const reason = (e as ApiError).details?.reason;
      setErr({ message: (e as ApiError).message, noCredits: reason === "ai_credits_insufficient" || reason === "ai_daily_limit" });
    } finally {
      setPending(false);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setStep(null);
          setOpen(true);
          trackAction("ai.panel_open");
        }}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-focus"
        aria-label="Perguntar à IA"
        title="Perguntar à IA"
      >
        <Sparkles className="h-[18px] w-[18px] text-brand" aria-hidden />
        <span className="hidden md:inline">IA</span>
      </button>
      <Drawer
        open={open}
        onOpenChange={setOpen}
        title="Pergunte à IA"
        description="Dúvidas sobre a configuração, o funil, o tráfego e os seus números. A IA explica o caminho; quem faz é você."
        footer={
          <form className="flex w-full items-end gap-2" onSubmit={(e) => { e.preventDefault(); void ask(); }}>
            <Textarea aria-label="Sua pergunta" rows={2} className="min-w-0 flex-1" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void ask(); } }} placeholder="Ex.: onde acho o endereço do CRM?" />
            <Button type="submit" loading={pending} aria-label="Enviar"><Send className="h-4 w-4" aria-hidden /></Button>
          </form>
        }
      >
        <div ref={listRef} className="flex flex-col gap-3 text-sm">
          {hist.data?.ai === "none" && <Alert tone="info">A IA não está configurada neste ambiente; as respostas podem ficar indisponíveis.</Alert>}
          {hist.data?.ai === "simulator" && <Alert tone="warning">IA simulada (ambiente de teste): respostas de exemplo baseadas no passo atual.</Alert>}
          {balance !== null && (
            <p className="flex items-center justify-between rounded-[var(--cm-radius)] bg-surface-muted px-3 py-2 text-xs text-muted">
              <span>Cada pergunta usa {hist.data!.credits!.per_question} crédito de IA · saldo: <strong className="text-fg tabular-nums">{Math.max(balance, 0)}</strong></span>
              <Link to="/app/billing#creditos-ia" className="font-medium text-brand hover:underline" onClick={() => setOpen(false)}>Recarregar</Link>
            </p>
          )}
          {messages.length === 0 && <p className="text-muted">Pergunte algo como “qual pixel escolher?”, “por que a Meta mostra mais leads do que o CRM?” ou “o que é marcar ganho?”.</p>}
          {messages.map((m, i) => (
            <div key={m.id ?? `l${i}`} className={m.role === "user" ? "ml-8 self-end rounded-lg bg-brand px-3 py-2 text-brand-fg" : "mr-8 whitespace-pre-wrap rounded-lg bg-surface-muted px-3 py-2"}>
              {m.content}
            </div>
          ))}
          {pending && <p className="text-xs text-muted">A IA está escrevendo…</p>}
          {err && (
            <Alert tone={err.noCredits ? "warning" : "danger"}>
              {err.message}
              {err.noCredits && <> <Link to="/app/billing#creditos-ia" className="font-medium underline" onClick={() => setOpen(false)}>Recarregar créditos</Link></>}
            </Alert>
          )}
        </div>
      </Drawer>
    </>
  );
}
